import { useTranslation } from 'react-i18next';
import { Card, Divider, Flex, Grid, Loader, Text, Title } from '@mantine/core';
import { PieChart } from '@mantine/charts';
import { useGetPaymentMethodStatisticsReport } from '../../../hooks/reports/queries';
import momentTz from 'moment-timezone';
import { CommonReportChartProps, graphColors } from '.';
import { useMemo } from 'react';
import { formatMoneyString } from '../../../utils/strings';
import moment from 'moment-timezone';

type PaymentMethod = 'card' | 'cash' | 'delivery' | 'transfer';
const PaymentMethodsChart = ({ dateRange, chartHeight }: CommonReportChartProps) => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const { data, isLoading } = useGetPaymentMethodStatisticsReport(searchParamStoreId, {
    fromDate: dateRange[0]?.toISOString(),
    toDate: dateRange[1]?.toISOString(),
    timezone: momentTz.tz.guess(true)
  });
  const chartConfig = {
    card: { color: graphColors[0].hex },
    cash: { color: graphColors[1].hex },
    delivery: { color: graphColors[2].hex },
    transfer: { color: graphColors[3].hex }
  };

  const chartData = useMemo(() => {
    const paymentMethodsData = {
      card: 0,
      cash: 0,
      delivery: 0,
      transfer: 0
    };

    if (data?.ordersReport) {
      Object.keys(data?.ordersReport).forEach((key: string) => {
        paymentMethodsData[key as PaymentMethod] = data?.ordersReport[key as PaymentMethod];
      });
    }
    const chartDataArr = Object.keys(paymentMethodsData).map((key: string) => {
      return {
        name: t(`FieldName:paymentMethods.${key}`),
        value: paymentMethodsData[key as PaymentMethod],
        color: chartConfig[key as PaymentMethod].color
      };
    });

    return chartDataArr;
  }, [data]);

  return (
    <Card h="100%">
      <Card.Section inheritPadding withBorder py="md">
        <Grid>
          <Grid.Col span={12}>
            <Title>{t('ReportsPage:paymentMethodsReport.title')}</Title>
            <Text c={'dimmed'}>
              {moment(dateRange[0]).format('DD MMMM YYYY')} -{' '}
              {moment(dateRange[1]).format('DD MMMM YYYY')}
            </Text>
          </Grid.Col>
          {isLoading && <Loader m={'auto'} />}
          {!isLoading && (
            <>
              <Grid.Col span={'content'} m={'auto'}>
                <PieChart
                  h={chartHeight}
                  size={200}
                  data={chartData}
                  withTooltip
                  tooltipDataSource="segment"
                  withLabelsLine
                  labelsPosition="outside"
                  labelsType="percent"
                  withLabels
                />
              </Grid.Col>
              <Grid.Col span={12}>
                {Object.keys(data?.ordersReport || {})?.map((paymentMethodKey) => {
                  return (
                    <>
                      <Divider size="sm" my="xs" />
                      <Flex justify="space-between" align="center">
                        <Text>{t(`FieldName:paymentMethods.${paymentMethodKey}`)}</Text>
                        <Text>
                          {formatMoneyString(
                            (
                              (data?.ordersReport || {})[paymentMethodKey as PaymentMethod] || 0
                            ).toFixed(2)
                          )}
                        </Text>
                      </Flex>
                    </>
                  );
                })}
              </Grid.Col>
            </>
          )}
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default PaymentMethodsChart;
